import React, { useState, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Lock, LogIn, ArrowRight, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';

/**
 * ProtectedRoute - Wraps routes that require authentication
 *
 * @param {React.ReactNode} children - The component to render if authenticated
 * @param {string[]} allowedRoles - Optional array of allowed account types (e.g., ['corporate', 'superadmin'])
 * @param {string} redirectTo - Where to redirect if not authenticated (defaults based on route)
 */
const ProtectedRoute = ({ children, allowedRoles = [], redirectTo }) => {
  const { user, profile, loading } = useAuth();
  const { effectiveAccountType, loading: companyLoading } = useCompany();
  const location = useLocation();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  // Determine login path based on current route
  const getLoginPath = () => {
    if (redirectTo) return redirectTo;
    if (location.pathname.startsWith('/super-admin')) return '/super-admin';
    if (location.pathname.startsWith('/contractor')) return '/contractor-login';
    if (location.pathname.startsWith('/corporate')) return '/corporate-login';
    return '/';
  };

  const loginPath = getLoginPath();

  // Get portal name for display
  const getPortalName = () => {
    if (location.pathname.startsWith('/super-admin')) return 'Admin Portal';
    if (location.pathname.startsWith('/contractor')) return 'Contractor Portal';
    if (location.pathname.startsWith('/corporate')) return 'Corporate Portal';
    return 'this area';
  };

  // Countdown and redirect effect for unauthenticated users
  useEffect(() => {
    if (!loading && !user) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setShouldRedirect(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [loading, user]);

  // Handle the actual redirect after countdown
  useEffect(() => {
    if (shouldRedirect) {
      navigate(loginPath, { state: { from: location }, replace: true });
    }
  }, [shouldRedirect, navigate, loginPath, location]);

  // Show loading state while checking authentication or loading company context
  if (loading || companyLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          <p className="mt-4 text-text-secondary">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - show access denied screen with countdown
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="relative">
          {/* Background glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-red-500/10 to-primary/10 rounded-3xl blur-3xl" />

          {/* Main card */}
          <div className="relative bg-surface border border-border rounded-2xl p-10 max-w-md text-center">
            {/* Lock icon */}
            <div className="mb-6">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-full flex items-center justify-center">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center">
                  <Lock size={28} color="white" />
                </div>
              </div>
            </div>

            {/* Message */}
            <h2 className="text-2xl font-bold text-text-primary mb-2">
              Hold Up!
            </h2>
            <p className="text-text-secondary mb-6">
              You need to sign in to access the {getPortalName()}.
              <br />
              <span className="text-text-secondary text-sm">Your session may have expired or you haven't logged in yet.</span>
            </p>

            {/* Countdown */}
            <div className="mb-6">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-background rounded-full">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold">
                  {countdown}
                </div>
                <span className="text-text-secondary text-sm">Redirecting to login...</span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col space-y-3">
              <button
                onClick={() => navigate(loginPath, { state: { from: location }, replace: true })}
                className="flex items-center justify-center space-x-2 w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all"
              >
                <LogIn size={18} />
                <span>Sign In Now</span>
                <ArrowRight size={18} />
              </button>

              <button
                onClick={() => navigate('/')}
                className="text-text-secondary hover:text-text-primary text-sm transition-colors"
              >
                Go to Home Page
              </button>
            </div>

            {/* Security note */}
            <div className="mt-6 pt-4 border-t border-border">
              <div className="flex items-center justify-center space-x-2 text-text-secondary text-xs">
                <Shield size={14} className="text-green-500" />
                <span>Protected by Mervo</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check role-based access if allowedRoles is specified
  // Uses effectiveAccountType which is derived from current company role
  // This allows users with different roles at different companies to access the correct portal
  if (allowedRoles.length > 0 && profile) {
    if (!allowedRoles.includes(effectiveAccountType)) {
      // User is authenticated but doesn't have the right role for current company
      // Redirect to their appropriate dashboard based on effective account type
      const dashboardPaths = {
        superadmin: '/super-admin/dashboard',
        corporate: '/corporate/dashboard',
        contractor: '/contractor/dashboard'
      };

      const userDashboard = dashboardPaths[effectiveAccountType] || '/';
      return <Navigate to={userDashboard} replace />;
    }
  }

  // Authenticated and authorized - render the protected content
  return children;
};

export default ProtectedRoute;
